import { useState } from "react";
import { Check, ShieldCheck } from "lucide-react";

import { api, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import OtpInput from "@/components/OtpInput";

/**
 * F4 — scaling-confirm flow for the SCALING_REVIEW state (SPEC §19.22).
 * After a >50% balance jump the bot freezes position size until the operator
 * acknowledges the new size. GUARDED control: request an emailed OTP, type the
 * new size + code, confirm. Never auto-fires; not an IG order.
 */
export default function ScalingConfirm() {
  const [open, setOpen] = useState(false);
  const [newSize, setNewSize] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const reset = () => {
    setNewSize("");
    setOtp("");
    setOtpSent(false);
    setError(null);
    setDone(false);
  };

  const sizeValue = Number(newSize);
  const sizeValid = newSize.trim() !== "" && Number.isFinite(sizeValue) && sizeValue > 0;

  const sendOtp = async () => {
    setPending(true);
    setError(null);
    try {
      await api.requestScalingOtp();
      setOtpSent(true);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 429
          ? "Trop de demandes de code — patiente puis réessaie."
          : "Impossible d'envoyer le code de confirmation.",
      );
    } finally {
      setPending(false);
    }
  };

  const confirm = async () => {
    setPending(true);
    setError(null);
    try {
      await api.confirmScaling({
        newSize: sizeValue,
        phrase: `SCALE TO ${sizeValue}`,
        otp,
      });
      setDone(true);
    } catch (e) {
      setError(mapError(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        onClick={() => {
          reset();
          setOpen(true);
        }}
        className="bg-warning text-black hover:bg-warning/90"
        data-testid="scaling-confirm-open"
      >
        Confirmer le scaling…
      </Button>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) reset();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="text-warning size-5" aria-hidden />
              Confirmer le scaling du capital
            </DialogTitle>
            <DialogDescription>
              Confirme la nouvelle taille par point — geste gardé, aucun ordre IG.
            </DialogDescription>
          </DialogHeader>

          {done ? (
            <p
              className="text-buy flex items-center gap-2 text-sm"
              role="status"
              data-testid="scaling-confirm-done"
            >
              <Check className="size-4" aria-hidden />
              Scaling confirmé. Le gel se lève au prochain poll d'état.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">
                  Nouvelle taille par point (€)
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.1"
                  value={newSize}
                  onChange={(e) => setNewSize(e.target.value)}
                  disabled={pending || otpSent}
                  data-testid="scaling-confirm-size"
                  className="field px-3 py-2 font-mono tabular-nums"
                />
              </label>

              {!otpSent ? (
                <Button
                  type="button"
                  onClick={sendOtp}
                  disabled={pending || !sizeValid}
                  data-testid="scaling-confirm-sendotp"
                >
                  {pending ? "Envoi…" : "M'envoyer un code par e-mail"}
                </Button>
              ) : (
                <div className="flex flex-col gap-2">
                  <span className="text-muted-foreground text-sm">
                    Entre le code à 6 chiffres reçu par e-mail, puis confirme{" "}
                    <code className="text-foreground">SCALE TO {sizeValue}</code>.
                  </span>
                  <OtpInput value={otp} onChange={setOtp} autoFocus />
                </div>
              )}

              {error && (
                <p className="text-loss text-sm" role="alert">
                  {error}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={pending}
            >
              {done ? "Fermer" : "Annuler"}
            </Button>
            {!done && otpSent && (
              <Button
                type="button"
                onClick={confirm}
                disabled={pending || otp.length !== 6 || !sizeValid}
                data-testid="scaling-confirm-submit"
                className="bg-warning text-black hover:bg-warning/90"
              >
                {pending ? "Confirmation…" : "Confirmer le scaling"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function mapError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return "Code ou phrase invalide. Rien n'a été modifié.";
    if (e.status === 409)
      return "Aucune revue de scaling en attente — rien à confirmer.";
    if (e.status === 429) return "Trop de tentatives — patiente puis réessaie.";
    return `Refusé (HTTP ${e.status}).`;
  }
  return "Requête échouée. Le scaling n'a pas été confirmé.";
}
